import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { apiProvider } from "../api/core/provider";
import { apiWorkAreas } from "../api/workareas";
import { loadGoogle } from "../components/InspectorRouteMap";
import { toast, ToastContainer } from "react-toastify";
import FSButton from "../components/Buttons/FSButton";
import { useTour } from "../tours/useTour";

export default function WorkAreas() {
  const [workAreas, setWorkAreas] = useState([]);
  const [editingArea, setEditingArea] = useState(null); // { id, name, dailyCapacity, counties }
  const [nameDraft, setNameDraft] = useState("");
  const [capacityDraft, setCapacityDraft] = useState(0);
  const [newAreaName, setNewAreaName] = useState("");
  const [newAreaCapacity, setNewAreaCapacity] = useState(0);
  // Anchor points: out-of-area drive times are measured from each one.
  // Pending row for "+ Add anchor": { lat, lng } as strings.
  const [newAnchorAddress, setNewAnchorAddress] = useState("");
  const [anchorPlace, setAnchorPlace] = useState(null); // chosen via autocomplete: {lat, lng}
  const [editingAnchor, setEditingAnchor] = useState(null); // { id, text } — chip being relabeled
  const anchorInputRef = useRef(null);
  const [allCounties, setAllCounties] = useState([]);
  const [countyStates, setCountyStates] = useState([]);
  const [selectedCountyState, setSelectedCountyState] = useState("");

  const loadWorkAreas = useCallback(() => {
    apiWorkAreas.list().then((response) => {
      setWorkAreas(response.data.result || []);
    }).catch(() => {
      toast.error("Error loading work areas");
    });
  }, []);

  useEffect(() => {
    loadWorkAreas();
  }, [loadWorkAreas]);

  useEffect(() => {
    if (allCounties.length > 0) {return;}
    apiProvider.get("counties").then((response) => {
      const counties = response.data.result || [];
      setAllCounties(counties);
      setCountyStates([...new Set(counties.map((c) => c.state))].sort());
    }).catch(() => {
      toast.error("Error loading counties");
    });
  }, [allCounties.length]);

  // Sync drafts when the selected area changes
  useEffect(() => {
    setNameDraft(editingArea?.name || "");
    setCapacityDraft(editingArea?.dailyCapacity || 0);
    setNewAnchorAddress("");
    setAnchorPlace(null);
  }, [editingArea]);

  const createArea = async () => {
    if (!newAreaName.trim()) {return;}
    try {
      await apiWorkAreas.create(newAreaName.trim(), Math.max(0, parseInt(newAreaCapacity, 10) || 0));
      setNewAreaName("");
      setNewAreaCapacity(0);
      loadWorkAreas();
      toast.success("Work area created");
    } catch {
      toast.error("Error creating work area");
    }
  };

  const saveName = async () => {
    if (!editingArea) {return;}
    const name = nameDraft.trim();
    if (!name || name === editingArea.name) {return;}
    try {
      await apiWorkAreas.update(editingArea.id, name, editingArea.dailyCapacity || 0);
      const updated = { ...editingArea, name };
      setEditingArea(updated);
      loadWorkAreas();
      toast.success("Area renamed");
    } catch {
      toast.error("Error renaming area");
    }
  };

  const saveCapacity = async () => {
    if (!editingArea) {return;}
    const v = Math.max(0, parseInt(capacityDraft, 10) || 0);
    if (v === (editingArea.dailyCapacity || 0)) {return;}
    try {
      await apiWorkAreas.update(editingArea.id, editingArea.name, v);
      setEditingArea({ ...editingArea, dailyCapacity: v });
      loadWorkAreas();
      toast.success("Capacity updated");
    } catch {
      toast.error("Error updating capacity");
      setCapacityDraft(editingArea.dailyCapacity || 0);
    }
  };

  const addAnchor = async () => {
    if (!editingArea) {return;}
    const text = (newAnchorAddress || "").trim();
    if (!text) {return;}
    // Literal "lat, long" pairs are accepted too; anything else gets geocoded.
    const m = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    let lat = 0, lng = 0;
    if (anchorPlace) {
      lat = anchorPlace.lat; lng = anchorPlace.lng;
    } else if (m) {
      lat = parseFloat(m[1]); lng = parseFloat(m[2]);
    }
    try {
      const res = await apiWorkAreas.addAnchor(editingArea.id, lat, lng, anchorPlace ? text : m ? "" : text);
      if (!res?.data || res.data.status !== 200) {
        // geocode/validate failure surfaces here (handleResponse never throws)
        toast.error(res?.data?.message || "Could not add that anchor point");
        return;
      }
      setNewAnchorAddress("");
      loadWorkAreas();
      setEditingArea({ ...editingArea, anchors: [...editingArea.anchors || [], res.data.result] });
      toast.success("Anchor point added");
    } catch {
      toast.error("Error adding anchor point");
    }
  };

  const saveAnchorEdit = async () => {
    if (!editingAnchor || !editingArea) {return;}
    const original = (editingArea.anchors || []).find((x) => x.id === editingAnchor.id);
    const text = (editingAnchor.text || "").trim();
    if (!text || text === (original?.address || "")) { setEditingAnchor(null); return; }
    try {
      const res = await apiWorkAreas.editAnchor(editingArea.id, editingAnchor.id, 0, 0, text);
      if (!res?.data || res.data.status !== 200) {
        toast.error(res?.data?.message || "Could not update that anchor point");
        return;
      }
      const updated = res.data.result;
      loadWorkAreas();
      setEditingArea({ ...editingArea, anchors: (editingArea.anchors || []).map((x) => x.id === editingAnchor.id ? updated : x) });
      toast.success("Anchor point updated");
      setEditingAnchor(null);
    } catch {
      toast.error("Error updating anchor point");
    }
  };

  const removeAnchor = async (anchorId) => {
    if (!editingArea) {return;}
    try {
      await apiWorkAreas.removeAnchor(editingArea.id, anchorId);
      loadWorkAreas();
      setEditingArea({ ...editingArea, anchors: (editingArea.anchors || []).filter((x) => x.id !== anchorId) });
      toast.success("Anchor point removed");
    } catch {
      toast.error("Error removing anchor point");
    }
  };

  const deleteArea = async (id) => {
    if (!window.confirm("Delete this work area? Inspectors using it will fall back to their default / the area default.")) {return;}
    try {
      await apiWorkAreas.remove(id);
      if (editingArea?.id === id) {setEditingArea(null);}
      loadWorkAreas();
      toast.success("Work area deleted");
    } catch {
      toast.error("Error deleting work area");
    }
  };

  const loadAreaCounties = async (area) => {
    try {
      const response = await apiWorkAreas.getCounties(area.id);
      setEditingArea({ ...area, counties: response.data.result || [] });
    } catch {
      toast.error("Error loading area counties");
    }
  };

  const addCountyToArea = async (countyId) => {
    if (!editingArea) {return;}
    try {
      await apiWorkAreas.addCounty(editingArea.id, countyId);
      loadAreaCounties(editingArea);
    } catch {
      toast.error("Error adding county");
    }
  };

  const addAllCountiesForState = async () => {
    if (!editingArea || !selectedCountyState) {return;}
    try {
      await apiWorkAreas.addAllCountiesForState(editingArea.id, selectedCountyState);
      loadAreaCounties(editingArea);
      toast.success(`Added all ${selectedCountyState} counties`);
    } catch {
      toast.error("Error adding counties for state");
    }
  };

  const removeCountyFromArea = async (countyId) => {
    if (!editingArea) {return;}
    try {
      await apiWorkAreas.removeCounty(editingArea.id, countyId);
      loadAreaCounties(editingArea);
    } catch {
      toast.error("Error removing county");
    }
  };

  const filteredCounties = selectedCountyState
    ? allCounties.filter((c) => c.state === selectedCountyState)
    : [];
  const editingCountyIds = new Set((editingArea?.counties || []).map((c) => c.county.id));

  const tourSteps = useMemo(() => [
    {
      element: '[data-tour="wa-title"]',
      popover: {
        title: "Work Areas",
        description: "Group counties into company-level areas, each with a daily inspection capacity. The scheduler uses these to route work.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="wa-intro"]',
      popover: {
        title: "What they're for",
        description: "Each area has a daily capacity and a set of counties. Inspectors are assigned to areas per weekday on the Inspector Availability page.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="wa-create"]',
      popover: {
        title: "Create an area",
        description: "Enter a name and daily capacity (0 = use the default of 20), then Create.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="wa-list"]',
      popover: {
        title: "Area list",
        description: "Click an area to edit it. The (n) shows its daily capacity; x deletes it.",
        side: "right", align: "start",
      },
    },
    {
      element: '[data-tour="wa-edit"]',
      popover: {
        title: "Edit an area",
        description: "Click an area on the left, then edit its name, daily capacity, and assigned counties here. Add counties one at a time, or a whole state at once.",
        side: "top", align: "start",
      },
    },
  ], []);

  const { startTour, showButton } = useTour({ tourKey: "work-areas", steps: tourSteps });

  // Places autocomplete on the anchor input: picking a suggestion takes the
  // place's coordinates directly (no guessing); server geocode stays fallback.
  useEffect(() => {
    if (!editingArea || !anchorInputRef.current) { return; } // input mounts only with the editor open
    let cancelled = false;
    loadGoogle().then((g) => {
      if (cancelled || !anchorInputRef.current) { return; }
      const ac = new g.maps.places.Autocomplete(anchorInputRef.current);
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place.geometry) {
          setAnchorPlace(place.geometry.location.toJSON());
        }
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [!!editingArea]);

  return (
    <div className="tw-max-w-7xl tw-mx-auto tw-p-6">
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar closeOnClick />
      <h1 data-tour="wa-title" className="tw-text-2xl tw-font-bold tw-mb-6">Work Areas</h1>
      <p data-tour="wa-intro" className="tw-text-sm tw-text-gray-600 tw-mb-6">
        Company-level inspection areas. Each area has a daily capacity (inspections/day) and
        a set of counties. Inspectors are assigned to areas per weekday on the{" "}
        <a className="tw-text-blue-600 hover:tw-text-blue-800 tw-underline" href="/inspector-availability">Inspector Availability</a> page.
      </p>

      {/* Create new area */}
      <div data-tour="wa-create" className="tw-flex tw-gap-2 tw-mb-6 tw-items-center">
        <input
          type="text"
          value={newAreaName}
          onChange={(e) => setNewAreaName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createArea()}
          placeholder="New area name..."
          className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-1.5 tw-text-sm tw-flex-1 tw-max-w-xs"
        />
        <input
          type="number"
          min={0}
          value={newAreaCapacity}
          onChange={(e) => setNewAreaCapacity(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createArea()}
          placeholder="cap"
          title="Daily capacity (0 = use default 20)"
          className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1.5 tw-text-sm tw-w-20"
        />
        <FSButton onClick={createArea} size="small">Create</FSButton>
        {showButton && <button onClick={startTour} className="tw-ml-auto tw-px-3 tw-py-1.5 tw-border tw-rounded tw-text-sm hover:tw-bg-gray-50">Take the tour</button>}
      </div>

      <div data-tour="wa-edit" className="tw-flex tw-gap-6">
        {/* Area list */}
        <div data-tour="wa-list" className="tw-w-56 tw-shrink-0">
          {workAreas.map((area) => 
            <div
              key={area.id}
              className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-rounded tw-cursor-pointer tw-text-sm ${
                editingArea?.id === area.id ? "tw-bg-blue-100 tw-text-blue-800" : "hover:tw-bg-gray-100"
              }`}
              onClick={() => loadAreaCounties(area)}
            >
              <span>{area.name} <span className={`tw-text-xs tw-text-gray-400 ${area.dailyCapacity > 0 ? "" : "tw-italic"}`} title={area.dailyCapacity > 0 ? "" : "using default capacity (20)"}>({area.dailyCapacity > 0 ? area.dailyCapacity : 20})</span></span>
              <button
                onClick={(e) => { e.stopPropagation(); deleteArea(area.id); }}
                className="tw-text-red-400 hover:tw-text-red-600 tw-text-xs"
                title="Delete area"
              >x</button>
            </div>
          )}
          {workAreas.length === 0 && <p className="tw-text-sm tw-text-gray-400 tw-px-3">No work areas yet</p>}
        </div>

        {/* County editor for selected area */}
        {editingArea && 
          <div className="tw-flex-1 tw-border-l tw-pl-6">
            <h3 className="tw-font-medium tw-mb-3">Editing {editingArea.name}</h3>

            {/* Name */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <label className="tw-text-xs tw-text-gray-600 tw-w-24">Name:</label>
              <input
                type="text"
                className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-48"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => { if (e.key === "Enter") {e.target.blur();} }}
              />
            </div>

            {/* Capacity */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
              <label className="tw-text-xs tw-text-gray-600 tw-w-24">Daily capacity:</label>
              <input
                type="number"
                min={0}
                className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-20"
                value={capacityDraft}
                onChange={(e) => setCapacityDraft(e.target.value)}
                onBlur={saveCapacity}
                onKeyDown={(e) => { if (e.key === "Enter") {e.target.blur();} }}
              />
              <span className="tw-text-xs tw-text-gray-500">{(editingArea.dailyCapacity || 0) === 0 ? "0 = use default (20)" : "inspections / day"}</span>
            </div>

            {/* Anchor points (out-of-area classification origins) */}
            <div className="tw-mb-4">
              <label className="tw-text-xs tw-text-gray-600">Anchor points (out-of-area drive times are measured from each):</label>
              <div className="tw-flex tw-flex-col tw-gap-1 tw-mt-1">
                {(editingArea.anchors || []).length === 0 &&
                  <span className="tw-text-xs tw-text-gray-400">No anchor points — area is skipped for out-of-area classification</span>}
                {(editingArea.anchors || []).map((a) => 
                  <span key={a.id} className="tw-flex tw-items-center tw-gap-2">
                    <span
                      className="tw-text-xs tw-font-mono tw-bg-blue-50 tw-text-blue-900 tw-border tw-border-blue-100 tw-rounded tw-px-2 tw-py-1 tw-cursor-pointer"
                      title={`Click to edit origin — anchor at ${a.lat.toFixed(5)}, ${a.lng.toFixed(5)}`}
                      onClick={() => setEditingAnchor({ id: a.id, text: a.address || "" })}
                    >
                      {editingAnchor?.id === a.id
                        ? <input
                          autoFocus
                          type="text"
                          className="tw-bg-white tw-border tw-border-blue-300 tw-rounded tw-px-1 tw-text-xs tw-w-56 tw-font-sans"
                          value={editingAnchor.text}
                          onChange={(e) => setEditingAnchor({ ...editingAnchor, text: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") {saveAnchorEdit();} if (e.key === "Escape") {setEditingAnchor(null);} }}
                          onBlur={saveAnchorEdit}
                          placeholder="Town / place / address (or lat, long)"
                        />
                        : a.address || `${a.lat.toFixed(4)}, ${a.lng.toFixed(4)}`}
                    </span>
                    <button
                      onClick={() => removeAnchor(a.id)}
                      className="tw-text-red-400 hover:tw-text-red-600 tw-text-xs"
                      title="Remove anchor point"
                    >x</button>
                  </span>
                )}
                <span className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="text"
                    ref={anchorInputRef}
                    className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-72"
                    value={newAnchorAddress}
                    onChange={(e) => { setNewAnchorAddress(e.target.value); setAnchorPlace(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") {addAnchor();} }}
                    placeholder="Town / place / address (or lat, long)"
                    title="Anchor origin: a town or address (geocoded), or literal 'lat, long'"
                  />
                  <button
                    onClick={addAnchor}
                    className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-underline"
                  >+ Add anchor</button>
                </span>
              </div>
            </div>

            {/* Current counties */}
            <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mb-3 tw-max-w-2xl">
              {(editingArea.counties || []).map((wac) => 
                <span
                  key={wac.county.id}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs"
                >
                  {wac.county.county}, {wac.county.state}
                  <button
                    onClick={() => removeCountyFromArea(wac.county.id)}
                    className="tw-text-blue-500 hover:tw-text-blue-700"
                  >x</button>
                </span>
              )}
              {(!editingArea.counties || editingArea.counties.length === 0) &&
                <span className="tw-text-xs tw-text-gray-400">No counties assigned</span>}
            </div>

            {/* Add county */}
            <div className="tw-flex tw-gap-2 tw-items-center">
              <select
                className="tw-border tw-rounded tw-px-2 tw-py-1 tw-text-xs"
                value={selectedCountyState}
                onChange={(e) => setSelectedCountyState(e.target.value)}
              >
                <option value="">State...</option>
                {countyStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {selectedCountyState && 
                <select
                  className="tw-border tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-w-48"
                  value=""
                  onChange={(e) => { if (e.target.value) {addCountyToArea(parseInt(e.target.value));} }}
                >
                  <option value="">Add county...</option>
                  {filteredCounties
                    .filter((c) => !editingCountyIds.has(c.id))
                    .map((c) => <option key={c.id} value={c.id}>{c.county}</option>)}
                </select>
              }
              {selectedCountyState && 
                <button
                  onClick={addAllCountiesForState}
                  className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-underline"
                  title={`Add all ${selectedCountyState} counties to this area`}
                >Add all in {selectedCountyState}</button>
              }
            </div>
          </div>
        }
      </div>
    </div>
  );
}